import { createRequiredLatinCharacterSchema } from '@/common/utils/latinCharacterValidation'
import z from 'zod'

const percentageSchema = z.number().min(0).max(100)

export const ironAndSteelAgents: { value: string; key: string }[] = [
  { key: 'coke', value: 'Coke' },
  { key: 'coal', value: 'Coal' },
  { key: 'fuelOil', value: 'Fuel oil' },
  { key: 'naturalGas', value: 'Natural gas' },
  { key: 'hydrogen', value: 'Hydrogen' },
  { key: 'charcoalOrBiomass', value: 'Charcoal or biomass' },
  { key: 'other', value: 'Other' }
]

const ironAndSteelAgentValues = ironAndSteelAgents.map(agent => agent.value) as [
  string,
  ...string[]
] // the casting is to make zod happy

export const qpd20Schema = z.object({
  value: z.number().gte(1).lte(5).nullable(),
  materials: z.array(
    z.object({
      name: createRequiredLatinCharacterSchema('Material name must use Latin characters', {
        min: 1,
        max: 255
      }),
      value: z.number().gte(1).lte(5)
    })
  )
})

export const ironAndSteelEmissionQualifyingParamsSchema = z.object({
  QPD10: z.object({
    value: z.enum(ironAndSteelAgentValues)
  }), // Reducing agent
  QPD11: z.object({
    value: z.enum(ironAndSteelAgentValues)
  }), // Precursor reducing Agent
  QPD12: z.object({
    value: percentageSchema
  }), // Carbon content of iron or steel
  QPD13: z.object({
    value: percentageSchema
  }), // Manganese content
  QPD14: z.object({
    value: percentageSchema
  }), // Chromium content
  QPD15: z.object({
    value: percentageSchema
  }), // Nickel content
  QPD16: z.object({
    value: percentageSchema
  }), // Other alloy elements content
  QPD17: z.object({
    value: z.number().min(0)
  }), // Steel scrap usage
  QPD18: z.object({
    value: percentageSchema
  }), // Steel  pre-consumer scrap percentage
  QPD19: z.object({
    value: z.number().min(0)
  }), // Steel PRODUCT scrap usage
  QPD20: qpd20Schema.optional() // Non-Iron content percentage
})

export const cementEmissionQualifyingParamsSchema = z.object({
  QPD02: z.object({
    value: percentageSchema
  }) // Clinker factor
})

export const calcinedClayEmissionQualifyingParamsSchema = z.object({
  QPD01: z.object({
    value: z.enum(['Calcinated', 'Not Calcinated'])
  }) // Calcination
})

export const qpd23Schema = z.object({
  value: z.number().gte(1).lte(5).nullable(),
  elements: z.array(
    z.object({
      name: createRequiredLatinCharacterSchema('Material name must use Latin characters', {
        min: 1,
        max: 255
      }),
      value: z.number().gte(1).lte(5)
    })
  )
})

export const aluminiumEmissionQualifyingParamsSchema = z.object({
  QPD21: z.object({
    value: z.number().min(0)
  }), // Aluminium scrap usage
  QPD22: z.object({
    value: percentageSchema
  }), // Aluminium pre-consumer scrap percentage
  QPD23: qpd23Schema
})

export type IronAndSteelEmissionQualifyingParams = z.infer<
  typeof ironAndSteelEmissionQualifyingParamsSchema
>
export type AluminiumEmissionQualifyingParams = z.infer<
  typeof aluminiumEmissionQualifyingParamsSchema
>
export type CementEmissionQualifyingParams = z.infer<typeof cementEmissionQualifyingParamsSchema>
export type CalcinedClayEmissionQualifyingParams = z.infer<
  typeof calcinedClayEmissionQualifyingParamsSchema
>

export const emissionQualifyingParamsSchema = z
  .object({
    ...ironAndSteelEmissionQualifyingParamsSchema.shape,
    ...cementEmissionQualifyingParamsSchema.shape,
    ...calcinedClayEmissionQualifyingParamsSchema.shape,
    ...aluminiumEmissionQualifyingParamsSchema.shape
  })
  .partial()

export type EmissionQualifyingParams = z.infer<typeof emissionQualifyingParamsSchema>
